import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Landmark,
  Wallet,
  KeyRound,
  Webhook,
  ShieldAlert,
  LifeBuoy,
  LogOut,
  Moon,
  Sun,
  Menu,
  BookOpen,
  Settings,
  Globe,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useTheme } from "@/providers/theme-provider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BrandRow } from "@/components/brand";
import { NotificationBell } from "@/components/notification-bell";
import { merchantApi } from "@/lib/api/merchant";
import { AccountSwitcher } from "@/components/account-switcher";

const NAV = [
  { to: "/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/markets", label: "Markets", icon: Globe },
  { to: "/orders/payin", label: "Transactions", icon: ArrowDownToLine },
  { to: "/orders/payout", label: "Payouts", icon: ArrowUpFromLine },
  { to: "/settlements", label: "Settlements", icon: Landmark },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/credentials", label: "API Keys", icon: KeyRound },
  { to: "/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/disputes", label: "Disputes", icon: ShieldAlert },
  { to: "/docs", label: "Documentation", icon: BookOpen },
  { to: "/support", label: "Support", icon: LifeBuoy },
  { to: "/settings", label: "Settings", icon: Settings },
];

/** BitPays merchant shell — dark sidebar (static on desktop, slide-in drawer on mobile). */
export function DashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const { data: profile } = useQuery({ queryKey: ["m-profile"], queryFn: merchantApi.profile });
  // Country-aware full API reference (Türkiye vs India).
  const docsUrl = ((profile?.merchant as any)?.country ?? "IN") === "TR"
    ? "https://bitpays.live/docs/turkey/"
    : "https://bitpays.live/docs/";

  const sidebar = (
    <>
      <div className="px-2 pb-5">
        <BrandRow on="dark" />
      </div>
      <nav className="flex flex-1 flex-col gap-0.5">
        {NAV.map((item) => {
          const active = pathname.startsWith(item.to);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors",
                active
                  ? "bg-white/10 text-white ring-1 ring-white/10 shadow-sm"
                  : "text-sidebar-foreground/70 hover:bg-white/[0.07] hover:text-white"
              )}
            >
              <Icon size={18} className={cn("shrink-0 transition-colors", active ? "text-primary" : "text-sidebar-foreground/50 group-hover:text-primary")} /> {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="rounded-lg bg-white/5 px-3 py-2.5 text-[11px] text-sidebar-foreground/60">
        bitpays.live · Merchant
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col bg-sidebar px-3 py-5 text-sidebar-foreground md:flex">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-sidebar px-3 py-5 text-sidebar-foreground shadow-xl">
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b bg-card/60 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <button
              className="-ml-1 rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={19} />
            </button>
            <span className="text-[13px] font-medium text-muted-foreground">Merchant Console</span>
          </div>
          <div className="flex items-center gap-1.5">
            <a href={docsUrl} target="_blank" rel="noopener noreferrer" title="API documentation">
              <Button variant="outline" size="sm"><BookOpen size={15} /> <span className="hidden sm:inline">API Docs</span></Button>
            </a>
            <AccountSwitcher />
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
